import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { MessageSquare, User } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import DashboardHeader from '../components/DashboardHeader';
import NavigationCarousel from '../components/NavigationCarousel';
import SearchBar from '../components/SearchBar';
import UploadModal from '../components/UploadModal';
import { uploadAssignment, getCourseSubmissions } from '../utils/uploadStorage';

const Study = () => {
  // const user = checkAuth(); // Unused variable
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('resources');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle URL parameters for direct tab linking
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['resources', 'courses', 'tests'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // useEffect moved after array definitions

  const tabs = [
    { id: 'resources', label: 'RESOURCES' },
    { id: 'courses', label: 'COURSES' },
    { id: 'tests', label: 'TESTS' },
  ];

  // Duplicate definitions removed - moved above

  const continueContent = [
    {
      title: 'Modern Communications Systems',
      image: '/assets/Modern Communications Systems.png',
      description: 'Advanced communication technologies for modern architecture',
      icon: '📡',
      iconColor: 'bg-gray-200',
      hoverIcon: 'bg-blue-500'
    },
    {
      title: 'A is for Architecture Podcast',
      image: '/assets/A IS FOR ARCHITECTURE.png',
      description: 'Weekly architectural insights and industry discussions',
      icon: '🎙️',
      iconColor: 'bg-gray-200',
      hoverIcon: 'bg-purple-500'
    },
    {
      title: 'Civil Engineering Construction',
      image: '/assets/Engineering Civil Construction.png',
      description: 'Fundamentals of construction engineering and project management',
      icon: '🏗️',
      iconColor: 'bg-gray-200',
      hoverIcon: 'bg-orange-500'
    }
  ];

  const assignedContent = [
    {
      title: 'AutoCAD 2024 for Civil Engineering Applications',
      image: '/assets/AutoCAD 2024 for Civil Engineering.png',
      description: 'Master AutoCAD for professional civil engineering projects',
      icon: '✏️',
      iconColor: 'bg-gray-200',
      hoverIcon: 'bg-red-500'
    },
    {
      title: 'Fifty Modern Buildings That Changed the World',
      image: '/assets/Fifty Modern Buildings.png',
      description: 'Explore iconic structures that revolutionized architecture',
      icon: '🏛️',
      iconColor: 'bg-gray-200',
      hoverIcon: 'bg-amber-500'
    },
    {
      title: 'Archispeak',
      image: '/assets/ARCHISPEAK.png',
      description: 'Professional architectural terminology and communication',
      icon: '📖',
      iconColor: 'bg-gray-200',
      hoverIcon: 'bg-green-500'
    }
  ];

  const aiSuggestedContent = [
    {
      title: 'Rapid Urbanization and Its Effects',
      image: '/assets/Rapid Urbanization.jpeg',
      description: 'AI-recommended course on environmental architecture and green building practices',
      icon: '🏙️',
      iconColor: 'bg-gray-200',
      hoverIcon: 'bg-emerald-500'
    },
    {
      title: 'Advanced Structural Analysis',
      image: '/assets/Structural analysis.jpeg',
      description: 'Personalized learning path for structural engineering fundamentals',
      icon: '⚙️',
      iconColor: 'bg-gray-200',
      hoverIcon: 'bg-indigo-500'
    },
    {
      title: 'AI & Machine Learning',
      image: '/assets/AI & Machine Learning.jpeg',
      description: 'Hands-on AI course covering smart city systems, predictive models, and automation.',
      icon: '🤖',
      iconColor: 'bg-gray-200',
      hoverIcon: 'bg-cyan-500',
      isCourseRedirect: true
    },
    {
      title: 'Civil Engineering Construction',
      image: '/assets/Engineering Civil Construction.png',
      description: 'Fundamentals of construction engineering and project management',
      icon: '🏗️',
      iconColor: 'bg-gray-200',
      hoverIcon: 'bg-orange-500'
    }
  ];

  // const aiTailoredContent = [ // Unused variable
  //   {
  //     title: '99% Invisible',
  //     image: '/assets/99_ INVISIBLE.png',
  //     description: 'Discover hidden design elements in everyday architecture'
  //   },
  //   {
  //     title: 'The Big Burn',
  //     image: '/assets/Big Burn.png',
  //     description: 'Environmental design lessons from historical disasters'
  //   },
  //   {
  //     title: 'Why Buildings Fall Down',
  //     image: '/assets/Why Buildings Fall Down.png',
  //     description: 'Structural engineering failures and safety principles'
  //   }
  // ];

  const selectedCourses = useMemo(() => [
    {
      title: 'INTRO TO SPATIAL REASONING',
      complete: '3 weeks',
      left: '9 weeks'
    },
    {
      title: 'BIM APPLI.: GIS MAPPING',
      complete: '7 weeks',
      left: '7 weeks'
    },
    {
      title: 'INTRO TO SUSTAINABILITY & DESIGN',
      complete: '3 weeks',
      left: '10 weeks'
    },
    {
      title: 'AI & MACHINE LEARNING',
      complete: '1 week',
      left: '13 weeks'
    }
  ], []);

  const aiAssignedCourses = useMemo(() => [
    {
      title: 'WHAT HAPPENS AFTER CONSTRUCTION? ENG334',
      complete: '4 weeks',
      left: '12 weeks'
    },
    {
      title: 'STRUCTURES I-III',
      complete: '15 weeks',
      left: '3 weeks'
    },
    {
      title: 'FOUNDATIONS OF GEOTECHNICAL ENGINEE-',
      complete: '3 weeks',
      left: '12 weeks'
    }
  ], []);

  // Load existing submissions on component mount
  useEffect(() => {
    const allCourses = [
      ...selectedCourses,
      ...aiAssignedCourses
    ];
    
    const submissions = {};
    allCourses.forEach(course => {
      const courseSubmissions = getCourseSubmissions(course.title);
      if (courseSubmissions.length > 0) {
        submissions[course.title] = courseSubmissions;
      }
    });
    
    setCourseSubmissions(submissions);
  }, [selectedCourses, aiAssignedCourses]);

  const selectedTests = [
    {
      title: 'SPATIAL REASONING 289',
      course: 'General Assessment',
      attempts: 3,
      highest: '76/110',
      status: 'available',
      dueDate: '2024-03-25'
    },
    {
      title: 'NCARB/A.R.E. MOCK-TEST',
      course: 'Professional Certification',
      attempts: 3,
      highest: 'PASS',
      status: 'completed',
      dueDate: '2024-03-20'
    }
  ];

  const aiAssignedTests = [
    {
      title: 'C.A.S.E. MOCK-TEST',
      course: 'AI Recommended',
      attempts: 4,
      highest: 'PASS',
      status: 'completed',
      dueDate: '2024-03-18'
    },
    {
      title: 'NAAB-accredited + A.X.P. + A.R.E. MOCK',
      course: 'AI Recommended',
      attempts: 2,
      highest: 'FAIL',
      status: 'available',
      dueDate: '2024-03-22'
    },
    {
      title: 'CSI CDT MOCK TEST',
      course: 'AI Recommended',
      attempts: 3,
      highest: 'FAIL',
      status: 'available',
      dueDate: '2024-03-28'
    }
  ];

  const courseAssignedTests = [
    {
      title: 'Midterm Exam - Spatial Reasoning',
      course: 'INTRO TO SPATIAL REASONING',
      attempts: 2,
      highest: '76/100',
      status: 'available',
      dueDate: '2024-03-15'
    },
    {
      title: 'BIM Software Proficiency Test',
      course: 'BIM APPLI.: GIS MAPPING',
      attempts: 1,
      highest: '85/100',
      status: 'available',
      dueDate: '2024-03-20'
    },
    {
      title: 'Facility Management Assessment',
      course: 'WHAT HAPPENS AFTER CONSTRUCTION',
      attempts: 0,
      highest: 'Not Taken',
      status: 'locked',
      dueDate: '2024-03-25'
    },
    {
      title: 'Structural Analysis Final Exam',
      course: 'STRUCTURES I-III',
      attempts: 3,
      highest: '92/100',
      status: 'completed',
      dueDate: '2024-03-10'
    },
    {
      title: 'Geotechnical Engineering Quiz',
      course: 'FOUNDATIONS OF GEOTECHNICAL',
      attempts: 1,
      highest: '78/100',
      status: 'available',
      dueDate: '2024-03-18'
    },
    {
      title: 'Sustainability Design Project',
      course: 'INTRO TO SUSTAINABILITY & DESIGN',
      attempts: 0,
      highest: 'Not Taken',
      status: 'locked',
      dueDate: '2024-03-22'
    }
  ];

  const [selectedResource, setSelectedResource] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseSubmissions, setCourseSubmissions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearch = (searchTerm) => {
    console.log('Searching for:', searchTerm);
    setSearchTerm(searchTerm);
  };

  // Comprehensive filter function that searches across all fields
  const filterContent = (contentArray, type = 'resource') => {
    if (!searchTerm) return contentArray;
    const lowerSearch = searchTerm.toLowerCase();
    return contentArray.filter(item => {
      // Search in title
      const titleMatch = item.title?.toLowerCase().includes(lowerSearch);
      // Search in description
      const descMatch = item.description?.toLowerCase().includes(lowerSearch);
      // Search in author if exists
      const authorMatch = item.author?.toLowerCase().includes(lowerSearch);
      // For tests: search in course field
      const courseMatch = item.course?.toLowerCase().includes(lowerSearch);
      
      return titleMatch || descMatch || authorMatch || courseMatch;
    });
  };

  // All content in one array for unified search results
  const allContent = {
    resources: [...continueContent, ...assignedContent, ...aiSuggestedContent],
    courses: [...selectedCourses, ...aiAssignedCourses],
    tests: [...selectedTests, ...aiAssignedTests, ...courseAssignedTests]
  };

  const handleResourceClick = (resource, type) => {
    if (resource.isCourseRedirect) {
      navigate('/app/course/ai-machine-learning');
      return;
    }
    setSelectedResource({ ...resource, type });
    setShowModal(true);
  };

  const handleTestClick = (test) => {
    console.log('Starting test:', test.title);
    // Simulate test starting
    alert(`Starting ${test.title}...\n\nThis would launch an interactive test with questions and real-time feedback.`);
  };

  const handleCourseClick = (course) => {
    console.log('Opening course:', course.title);
    // Map course titles to their corresponding IDs
    const courseIdMap = {
      'INTRO TO SPATIAL REASONING': 'intro-spatial-reasoning',
      'BIM APPLI.: GIS MAPPING': 'bim-gis-mapping',
      'WHAT HAPPENS AFTER CONSTRUCTION? ENG334': 'what-happens-after-construction',
      'STRUCTURES I-III': 'structures-i-iii',
      'FOUNDATIONS OF GEOTECHNICAL ENGINEE-': 'foundations-geotechnical',
      'INTRO TO SUSTAINABILITY & DESIGN': 'intro-sustainability-design',
      'AI & MACHINE LEARNING': 'ai-machine-learning'
    };
    
    const courseId = courseIdMap[course.title] || course.title.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/^-|-$/g, '');
    
    console.log('Navigating to course ID:', courseId);
    navigate(`/app/course/${courseId}`);
  };

  const handleUploadSubmit = async (submissionData) => {
    try {
      const result = await uploadAssignment(submissionData);
      console.log('Upload successful:', result);
      
      // Update course submissions state
      const submissions = getCourseSubmissions(submissionData.courseTitle);
      setCourseSubmissions(prev => ({
        ...prev,
        [submissionData.courseTitle]: submissions
      }));
      
      return result;
    } catch (error) {
      console.error('Upload failed:', error);
      throw error;
    }
  };

  const handleUploadModalClose = () => {
    setShowUploadModal(false);
    setSelectedCourse(null);
  };

  const handleNavigationClick = (item) => {
    if (item.isLogout) {
      navigate(item.path);
    } else {
      navigate(item.path);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const menuItems = [
    { label: 'HOME', path: '/app' },
    { label: 'STUDY', path: '/app/study' },
    { label: 'CHALLENGES', path: '/app/challenges' },
    { label: 'COMMUNITY', path: '/app/community' },
    { label: 'PIN UP', path: '/app/pinup' },
    { label: 'PROGRESS', path: '/app/progress' },
    { label: 'PROFILE', path: '/app/profile' },
    { label: 'LOG OUT', path: '/login', isLogout: true }
  ];

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Sidebar Navigation */}
      <div className="fixed top-0 left-0 h-screen z-40">
        <NavigationCarousel
          menuItems={menuItems}
          onItemClick={handleNavigationClick}
          isCollapsed={isSidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
      </div>

      {/* Main Content - Adjusted for Sidebar */}
      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'ml-20' : 'ml-56'}`}>
        {/* Dashboard Header */}
        <DashboardHeader title="STUDY" isSidebarCollapsed={isSidebarCollapsed} />

        {/* Search Section */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className="max-w-7xl px-6 py-4" style={{ 
            marginLeft: isSidebarCollapsed ? '20px' : '20px',
            transition: 'margin-left 0.3s ease'
          }}>
            <SearchBar 
              placeholder="Search resources, courses, tests..." 
              onSearch={handleSearch}
            />
          </div>
        </div>

        {/* Search Results Section */}
        {searchTerm && (
          <div className="max-w-7xl px-6 py-6" style={{ 
            marginLeft: isSidebarCollapsed ? '20px' : '20px',
            transition: 'margin-left 0.3s ease'
          }}>
            <div className="mb-4">
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Search Results for "{searchTerm}"
              </h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Found {filterContent(allContent.resources).length + filterContent(allContent.courses).length + filterContent(allContent.tests).length} results
              </p>
            </div>

            {/* Resources Results */}
            {filterContent(allContent.resources).length > 0 && (
              <section className="mb-6">
                <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Resources ({filterContent(allContent.resources).length})
                </h3>
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl p-4 border`}>
                  <div className="grid grid-cols-3 gap-4">
                    {filterContent(allContent.resources).map((item, idx) => (
                      <div 
                        key={idx} 
                        className="cursor-pointer"
                        onClick={() => handleResourceClick(item, 'search')}
                      >
                        <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-md transition-shadow">
                          <img 
                            src={item.image} 
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <p className={`text-sm text-center mt-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} line-clamp-2`}>
                          {item.title}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Courses Results */}
            {filterContent(allContent.courses).length > 0 && (
              <section className="mb-6">
                <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Courses ({filterContent(allContent.courses).length})
                </h3>
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl p-4 border`}>
                  <div className="space-y-2">
                    {filterContent(allContent.courses).map((course, idx) => {
                      const submissions = courseSubmissions[course.title] || [];
                      const hasSubmissions = submissions.length > 0;
                      
                      return (
                        <div 
                          key={idx} 
                          className={`p-3 flex items-center justify-between hover:shadow-md transition-all cursor-pointer rounded-lg ${
                            hasSubmissions ? 'ring-2 ring-green-200 bg-green-50' : isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}
                          onClick={() => handleCourseClick(course)}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className={`font-semibold text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {course.title}
                              </h4>
                              {hasSubmissions && (
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                                  {submissions.length} submission{submissions.length > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-4">
                            <div className="text-center">
                              <p className={`text-xs uppercase mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Complete</p>
                              <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{course.complete}</p>
                            </div>
                            <div className="text-center">
                              <p className={`text-xs uppercase mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Left</p>
                              <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{course.left}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            )}

            {/* Tests Results */}
            {filterContent(allContent.tests).length > 0 && (
              <section className="mb-6">
                <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Tests ({filterContent(allContent.tests).length})
                </h3>
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl p-4 border`}>
                  <div className="space-y-2">
                    {filterContent(allContent.tests).map((test, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 flex items-center justify-between hover:shadow-md transition-all cursor-pointer rounded-lg ${
                          test.status === 'locked' ? 'opacity-60 cursor-not-allowed' : isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                        }`}
                        onClick={() => test.status !== 'locked' && handleTestClick(test)}
                      >
                        <div className="flex-1">
                          <h4 className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {test.title}
                          </h4>
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{test.course}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              test.status === 'completed' ? 'bg-green-100 text-green-800' :
                              test.status === 'available' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {test.status.toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-4">
                          <div className="text-center">
                            <p className={`text-xs uppercase mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Attempts</p>
                            <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{test.attempts}</p>
                          </div>
                          <div className="text-center min-w-[80px]">
                            <p className={`text-xs uppercase mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Highest</p>
                            <p className={`text-sm font-bold ${
                              test.highest === 'Not Taken' ? 'text-gray-400' :
                              test.highest.includes('/') ? 'text-blue-600' :
                              test.highest === 'PASS' ? 'text-green-600' :
                              test.highest === 'FAIL' ? 'text-red-600' :
                              'text-gray-900'
                            }`}>
                              {test.highest}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* No Results */}
            {filterContent(allContent.resources).length === 0 && 
             filterContent(allContent.courses).length === 0 && 
             filterContent(allContent.tests).length === 0 && (
              <div className="text-center py-12">
                <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  No results found for "{searchTerm}"
                </p>
                <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Try a different search term
                </p>
              </div>
            )}
          </div>
        )}
      
      {/* Tab Navigation - Switch Tab */}
      <div className={`${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} border-t`}>
        <div className="max-w-6xl px-6" style={{ 
          marginLeft: '20px',
          transition: 'margin-left 0.3s ease'
        }}>
          <div className="flex">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex-1 py-6 font-bold text-sm uppercase tracking-wider transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'text-[#AC5757]'
                    : isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <span className="relative z-10">{tab.label}</span>
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#AC5757] rounded-t-lg"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl px-6 py-8" style={{ 
        marginLeft: isSidebarCollapsed ? '20px' : '20px',
        transition: 'margin-left 0.3s ease'
      }}>
        {/* Resources Tab Content */}
        {activeTab === 'resources' && (
          <div className="space-y-12">
            {/* CONTINUE Section */}
            <section>
              <h2 className={`font-judson text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-6 text-center`}>CONTINUE</h2>
              <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl p-6 border`}>
                <div className="grid grid-cols-3 gap-4">
                  {filterContent(continueContent).map((item, idx) => (
                    <div 
                      key={idx} 
                      className="cursor-pointer"
                      onClick={() => handleResourceClick(item, 'continue')}
                    >
                      <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-md transition-shadow">
                        <img 
                          src={item.image} 
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <p className={`text-base text-center mt-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} line-clamp-2`}>{item.title}</p>
                    </div>
                  ))}
                  {filterContent(continueContent).length === 0 && (
                    <div className="col-span-3 text-center py-8">
                      <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No results found</p>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* ASSIGNED Section */}
            <section>
              <h2 className={`font-judson text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-6 text-center`}>ASSIGNED</h2>
              <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl p-6 border`}>
                <div className="grid grid-cols-3 gap-4">
                  {filterContent(assignedContent).map((item, idx) => (
                    <div 
                      key={idx} 
                      className="cursor-pointer"
                      onClick={() => handleResourceClick(item, 'assigned')}
                    >
                      <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all relative">
                        <img 
                          src={item.image} 
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                        {/* Icon overlay that appears on hover */}
                        <div className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
                          item.hoverIcon || 'bg-red-500'
                        } opacity-0 group-hover:opacity-90`}>
                          <span className="text-8xl drop-shadow-lg">{item.icon}</span>
                        </div>
                      </div>
                      <p className={`text-base text-center mt-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} line-clamp-2`}>{item.title}</p>
                    </div>
                  ))}
                  {filterContent(assignedContent).length === 0 && (
                    <div className="col-span-3 text-center py-8">
                      <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No results found</p>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* AI-SUGGESTED Section */}
            <section>
              <h2 className={`font-judson text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-6 text-center`}>AI-SUGGESTED</h2>
              <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl p-6 border`}>
                <div className="grid grid-cols-3 gap-4">
                  {filterContent(aiSuggestedContent).map((item, idx) => (
                    <div 
                      key={idx} 
                      className="cursor-pointer"
                      onClick={() => handleResourceClick(item, 'ai-suggested')}
                    >
                      <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all relative">
                        <img 
                          src={item.image} 
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                        {/* Icon overlay that appears on hover */}
                        <div className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
                          item.hoverIcon || 'bg-emerald-500'
                        } opacity-0 group-hover:opacity-90`}>
                          <span className="text-8xl drop-shadow-lg">{item.icon}</span>
                        </div>
                      </div>
                      <p className={`text-base text-center mt-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} line-clamp-2`}>{item.title}</p>
                    </div>
                  ))}
                  {filterContent(aiSuggestedContent).length === 0 && (
                    <div className="col-span-3 text-center py-8">
                      <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No results found</p>
                    </div>
                  )}
                </div>
              </div>
            </section>

          </div>
        )}

        {/* Tests Tab Content */}
        {activeTab === 'tests' && (
          <div className="space-y-10">
            {/* COURSE ASSIGNED Tests */}
            <section>
              <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>COURSE ASSIGNED</h2>
              <div className="space-y-4">
                {courseAssignedTests.map((test, idx) => (
                  <div 
                    key={idx} 
                    className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-5 flex items-center justify-between hover:shadow-lg transition-shadow cursor-pointer ${
                      test.status === 'locked' ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                    onClick={() => test.status !== 'locked' && handleTestClick(test)}
                  >
                    <div className="flex-1">
                      <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{test.title}</h3>
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{test.course}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-xs text-gray-500">Due: {test.dueDate}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          test.status === 'completed' ? 'bg-green-100 text-green-800' :
                          test.status === 'available' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {test.status.toUpperCase()}
                        </span>
      </div>
                    </div>
                    <div className="flex gap-8">
                      <div className="text-center">
                        <p className={`text-xs uppercase mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>ATTEMPTS</p>
                        <p className={`text-xl font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`}>{test.attempts}</p>
                      </div>
                      <div className="text-center min-w-[100px]">
                        <p className={`text-xs uppercase mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>HIGHEST</p>
                        <p className={`text-xl font-bold ${
                          test.highest === 'Not Taken' ? isDarkMode ? 'text-gray-500' : 'text-gray-400' :
                          test.highest.includes('/') ? 'text-blue-600' :
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {test.highest}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* SELECTED Tests */}
            <section>
              <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>SELECTED</h2>
              <div className="space-y-4">
                {selectedTests.map((test, idx) => (
                  <div 
                    key={idx} 
                    className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-5 flex items-center justify-between hover:shadow-lg transition-shadow cursor-pointer ${
                      test.status === 'locked' ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                    onClick={() => test.status !== 'locked' && handleTestClick(test)}
                  >
                    <div className="flex-1">
                      <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{test.title}</h3>
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{test.course}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Due: {test.dueDate}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          test.status === 'completed' ? 'bg-green-100 text-green-800' :
                          test.status === 'available' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
            }`}>
                          {test.status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-8">
                      <div className="text-center">
                        <p className={`text-xs uppercase mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>ATTEMPTS</p>
                        <p className={`text-xl font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`}>{test.attempts}</p>
                      </div>
                      <div className="text-center min-w-[100px]">
                        <p className={`text-xs uppercase mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>HIGHEST</p>
                        <p className={`text-xl font-bold ${
                          test.highest === 'Not Taken' ? isDarkMode ? 'text-gray-500' : 'text-gray-400' :
                          test.highest.includes('/') ? 'text-blue-600' :
                          test.highest === 'PASS' ? 'text-green-600' :
                          test.highest === 'FAIL' ? 'text-red-600' :
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {test.highest}
                        </p>
              </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* AI-ASSIGNED Tests */}
            <section>
              <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>AI-ASSIGNED</h2>
              <div className="space-y-4">
                {aiAssignedTests.map((test, idx) => (
                  <div 
                    key={idx} 
                    className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-5 flex items-center justify-between hover:shadow-lg transition-shadow cursor-pointer ${
                      test.status === 'locked' ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                    onClick={() => test.status !== 'locked' && handleTestClick(test)}
                  >
                    <div className="flex-1">
                      <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{test.title}</h3>
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{test.course}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Due: {test.dueDate}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          test.status === 'completed' ? 'bg-green-100 text-green-800' :
                          test.status === 'available' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {test.status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-8">
                      <div className="text-center">
                        <p className={`text-xs uppercase mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>ATTEMPTS</p>
                        <p className={`text-xl font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`}>{test.attempts}</p>
                      </div>
                      <div className="text-center min-w-[100px]">
                        <p className={`text-xs uppercase mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>HIGHEST</p>
                        <p className={`text-xl font-bold ${
                          test.highest === 'Not Taken' ? isDarkMode ? 'text-gray-500' : 'text-gray-400' :
                          test.highest.includes('/') ? 'text-blue-600' :
                          test.highest === 'PASS' ? 'text-green-600' :
                          test.highest === 'FAIL' ? 'text-red-600' :
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {test.highest}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {/* Courses Tab Content */}
        {activeTab === 'courses' && (
          <div className="space-y-4">
            {/* SELECTED Courses */}
            <section>
              <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>SELECTED</h2>
              <div className="space-y-4">
                {selectedCourses.map((course, idx) => {
                  const submissions = courseSubmissions[course.title] || [];
                  const hasSubmissions = submissions.length > 0;
                  
                  return (
                    <div 
                      key={idx} 
                      className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-5 flex items-center justify-between hover:shadow-lg transition-shadow cursor-pointer ${
                        hasSubmissions ? 'ring-2 ring-green-200' : ''
                      } ${hasSubmissions && !isDarkMode ? 'bg-green-50' : ''}`}
                      onClick={() => handleCourseClick(course)}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className={`font-semibold text-sm md:text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{course.title}</h3>
                          {hasSubmissions && (
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                              {submissions.length} submission{submissions.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-8">
                        <div className="text-center">
                          <p className={`text-xs uppercase mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>COMPLETE</p>
                          <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{course.complete}</p>
                        </div>
                        <div className="text-center min-w-[80px]">
                          <p className={`text-xs uppercase mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>LEFT</p>
                          <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{course.left}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* AI-ASSIGNED Courses */}
            <section>
              <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>AI-ASSIGNED</h2>
              <div className="space-y-4">
                {aiAssignedCourses.map((course, idx) => {
                  const submissions = courseSubmissions[course.title] || [];
                  const hasSubmissions = submissions.length > 0;
                  
                  return (
                    <div 
                      key={idx} 
                      className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-5 flex items-center justify-between hover:shadow-lg transition-shadow cursor-pointer ${
                        hasSubmissions ? 'ring-2 ring-green-200' : ''
                      } ${hasSubmissions && !isDarkMode ? 'bg-green-50' : ''}`}
                      onClick={() => handleCourseClick(course)}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className={`font-semibold text-sm md:text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{course.title}</h3>
                          {hasSubmissions && (
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                              {submissions.length} submission{submissions.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-8">
                        <div className="text-center">
                          <p className={`text-xs uppercase mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>COMPLETE</p>
                          <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{course.complete}</p>
                        </div>
                        <div className="text-center min-w-[80px]">
                          <p className={`text-xs uppercase mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>LEFT</p>
                          <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{course.left}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

          </div>
        )}

      </div>

      {/* Resource Detail Modal */}
      {showModal && selectedResource && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">{selectedResource.title}</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="mb-4">
                <img
                  src={selectedResource.image}
                  alt={selectedResource.title}
                className="w-full h-48 object-cover rounded-lg"
                />
              </div>
            <p className="text-gray-600 mb-4">
              {selectedResource.type === 'continue' ? 'Continue where you left off' : 'New assignment from your instructor'}
            </p>
            <div className="flex gap-3">
              <button 
                onClick={() => {
                  setShowModal(false);
                  alert(`Opening ${selectedResource.title}...\n\nThis would launch the full resource with interactive content, videos, and exercises.`);
                }}
                className="flex-1 bg-[#AC5757] text-white py-2 px-4 rounded-lg font-semibold hover:bg-[#8A4A4A] transition-colors"
                  >
                    Open Resource
              </button>
              <button 
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && selectedCourse && (
      <UploadModal
        isOpen={showUploadModal}
        onClose={handleUploadModalClose}
          courseTitle={selectedCourse.title}
        onSubmit={handleUploadSubmit}
      />
      )}
      </div>
    </div>
  );
};

export default Study;